import { format as formatDate } from "date-fns";
import { Calendar as CalendarIcon, Check, ChevronDown, ChevronRight, RefreshCw } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { DateRange } from "react-day-picker";
import { useLocation, useNavigate } from "react-router-dom";
import { Spinner } from "@/components/ui/spinner";
import { Calendar } from "@/components/ui/calendar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { AllRequestsTable } from "@/components/UnderwritingDashboard/AllRequestsTable";
import { TeamFilterSelect } from "@/components/UnderwritingDashboard";
import { underwritingService } from "@/services/UnderwritingService";
import { useAppData } from "@/context/AppDataContext";
import { useApi } from "@/hooks/useApi";
import { useTeamFilter } from "@/hooks/useTeamFilter";
import { UnderwritingActivity, BusinessFilter, QueueCustomRange, QueuePeriod } from "@/models/underwriting";
import { computeQueuePeriodRange, businessToChannel } from "@/helpers/underwritingDashboard";

const BUSINESS_OPTIONS: BusinessFilter[] = ["Whole Business", "Consumer Direct", "Dealer Direct"];

const QUEUE_PERIOD_OPTIONS: { value: QueuePeriod; label: string }[] = [
  { value: "Today", label: "Today" },
  { value: "Last7Days", label: "Last 7 days" },
  { value: "Last30Days", label: "Last 30 days" },
  { value: "Custom", label: "Custom range" },
];

const AUTO_REFRESH_SECS = 180;

function formatUpdatedAgo(seconds: number): string {
  if (seconds < 5) return "just now";
  if (seconds < 60) return `${seconds}s ago`;
  const mins = Math.floor(seconds / 60);
  return `${mins}m ago`;
}

export function AllRequests() {
  const location = useLocation();
  const navigate = useNavigate();
  const { currentUser } = useAppData();
  const teamFilter = useTeamFilter();

  const navState = location.state as
    | { business?: BusinessFilter; queuePeriod?: QueuePeriod; queueCustomRange?: QueueCustomRange | null }
    | null;

  const [business, setBusiness] = useState<BusinessFilter>(navState?.business ?? "Whole Business");
  const [queuePeriod, setQueuePeriod] = useState<QueuePeriod>(navState?.queuePeriod ?? "Last7Days");
  const [queueCustomRange, setQueueCustomRange] = useState<QueueCustomRange | null>(navState?.queueCustomRange ?? null);
  const [periodPopoverOpen, setPeriodPopoverOpen] = useState(false);
  const [showCustomCalendar, setShowCustomCalendar] = useState(false);
  const [queueDraftRange, setQueueDraftRange] = useState<DateRange | undefined>(() =>
    navState?.queueCustomRange
      ? {
          from: new Date(`${navState.queueCustomRange.start}T00:00:00`),
          to: new Date(`${navState.queueCustomRange.end}T00:00:00`),
        }
      : undefined
  );
  const [refreshKey, setRefreshKey] = useState(0);

  const { start: queueStart, end: queueEnd } = useMemo(
    () => computeQueuePeriodRange(queuePeriod, queueCustomRange),
    [queuePeriod, queueCustomRange]
  );

  const activityResult = useApi<UnderwritingActivity[]>(
    (signal) =>
      underwritingService.getRecentActivity(
        {
          periodStart: `${queueStart}T00:00:00`,
          periodEnd: `${queueEnd}T23:59:59`,
          includeActioned: true,
        },
        signal
      ),
    [queueStart, queueEnd, refreshKey]
  );

  const channel = businessToChannel(business);

  const activities = useMemo(() => {
    let rows = activityResult.data ?? [];
    if (channel) rows = rows.filter((a) => a.channel === channel);
    rows = teamFilter.filterActivities(rows);
    return rows;
  }, [activityResult.data, channel, teamFilter]);

  const handleRefresh = useCallback(() => {
    setRefreshKey((k) => k + 1);
  }, []);

  const handleClaim = useCallback(
    async (activity: UnderwritingActivity) => {
      if (!currentUser?.name) return;
      await underwritingService.claimRequest(activity, currentUser.name);
      navigate(`/underwriting-queue/${activity.applicationId}`, {
        state: { activity, justClaimed: true, origin: { path: "/underwriting-queue/all-requests", label: "All Requests" } },
      });
    },
    [currentUser, navigate]
  );

  // ─── Auto-refresh every 3 minutes + "Updated x ago" indicator ───────────────
  const [lastUpdated, setLastUpdated] = useState(() => Date.now());
  const [now, setNow] = useState(() => Date.now());
  const tickRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    if (tickRef.current) clearInterval(tickRef.current);
    tickRef.current = setInterval(() => {
      if (periodPopoverOpen) return;
      setNow(Date.now());
      if ((Date.now() - lastUpdated) / 1000 >= AUTO_REFRESH_SECS) {
        handleRefresh();
        setLastUpdated(Date.now());
      }
    }, 1000);
    return () => {
      if (tickRef.current) clearInterval(tickRef.current);
    };
  }, [handleRefresh, lastUpdated, periodPopoverOpen]);

  const handleManualRefresh = useCallback(() => {
    handleRefresh();
    setLastUpdated(Date.now());
    setNow(Date.now());
  }, [handleRefresh]);

  const updatedLabel = formatUpdatedAgo(Math.floor((now - lastUpdated) / 1000));

  const periodLabel = useMemo(() => {
    if (queuePeriod !== "Custom" || !queueCustomRange) {
      return QUEUE_PERIOD_OPTIONS.find((p) => p.value === queuePeriod)?.label ?? "Last 7 days";
    }
    const from = new Date(`${queueCustomRange.start}T00:00:00`);
    const to = new Date(`${queueCustomRange.end}T00:00:00`);
    return `${formatDate(from, "dd MMM")} - ${formatDate(to, "dd MMM")}`;
  }, [queuePeriod, queueCustomRange]);

  const handleQueuePeriodChange = (nextPeriod: QueuePeriod) => {
    if (nextPeriod === "Custom") {
      setQueuePeriod("Custom");
      if (queueCustomRange) {
        setQueueDraftRange({
          from: new Date(`${queueCustomRange.start}T00:00:00`),
          to: new Date(`${queueCustomRange.end}T00:00:00`),
        });
      }
      setShowCustomCalendar(true);
      setPeriodPopoverOpen(true);
      return;
    }

    setQueuePeriod(nextPeriod);
    setShowCustomCalendar(false);
    setPeriodPopoverOpen(false);
  };

  const handleQueueCustomApply = () => {
    if (!queueDraftRange?.from || !queueDraftRange?.to) return;
    const from = queueDraftRange.from <= queueDraftRange.to ? queueDraftRange.from : queueDraftRange.to;
    const to = queueDraftRange.from <= queueDraftRange.to ? queueDraftRange.to : queueDraftRange.from;

    setQueueCustomRange({
      start: formatDate(from, "yyyy-MM-dd"),
      end: formatDate(to, "yyyy-MM-dd"),
    });
    setQueuePeriod("Custom");
    setShowCustomCalendar(false);
    setPeriodPopoverOpen(false);
  };

  return (
    <div className="flex h-full flex-col bg-zinc-100">
      {/* Page header */}
      <div className="flex items-center justify-between border-b border-border bg-card px-6 py-3">
        <nav className="flex items-center gap-1.5 text-sm">
          <button
            onClick={() => navigate("/underwriting-queue")}
            className="text-muted-foreground hover:text-foreground transition-colors"
          >
            Underwriting Queue
          </button>
          <ChevronRight className="size-3.5 text-muted-foreground" />
          <span className="font-semibold text-foreground">All Requests</span>
        </nav>

        <div className="flex items-center gap-2">
          {/* Business filter */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="flex items-center gap-1.5 rounded-md border border-border bg-card px-3 py-1.5 text-sm text-foreground hover:bg-muted">
                <span className="text-muted-foreground">Business:</span>
                <span className="font-medium">{business}</span>
                <ChevronDown className="size-3.5 text-muted-foreground" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="min-w-44">
              {BUSINESS_OPTIONS.map((opt) => (
                <DropdownMenuItem key={opt} onClick={() => setBusiness(opt)} className="justify-between">
                  <span className={business === opt ? "font-semibold" : ""}>{opt}</span>
                  {business === opt && <Check className="size-4" />}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>

          {/* Team filter (multi-select, grouped by Head of Sales) */}
          <TeamFilterSelect teamFilter={teamFilter} />

          {/* Date period filter */}
          <Popover
            open={periodPopoverOpen}
            onOpenChange={setPeriodPopoverOpen}
          >
            <PopoverTrigger asChild>
              <button className="flex items-center gap-1.5 rounded-md border border-border bg-card px-3 py-1.5 text-sm text-foreground hover:bg-muted">
                <CalendarIcon className="size-3.5 text-muted-foreground" />
                <span className="font-medium">{periodLabel}</span>
                <ChevronDown className="size-3.5 text-muted-foreground" />
              </button>
            </PopoverTrigger>
            <PopoverContent align="end" className="w-auto p-0">
              {!showCustomCalendar ? (
                <div className="w-40 p-1">
                  {QUEUE_PERIOD_OPTIONS.map((opt) => (
                    <button
                      key={opt.value}
                      onClick={() => handleQueuePeriodChange(opt.value)}
                      className="flex w-full items-center justify-between rounded-sm px-2 py-1.5 text-left text-sm hover:bg-muted"
                    >
                      <span className={queuePeriod === opt.value ? "font-semibold" : ""}>{opt.label}</span>
                      {queuePeriod === opt.value && <Check className="size-4" />}
                    </button>
                  ))}
                </div>
              ) : (
                <>
                  <div className="border-b px-3 py-2 text-sm font-medium text-foreground">Select date range</div>
                  <Calendar
                    mode="range"
                    selected={queueDraftRange}
                    onSelect={(range) => setQueueDraftRange(range)}
                    numberOfMonths={1}
                    captionLayout="dropdown"
                  />
                  <div className="flex items-center justify-end gap-2 border-t p-3">
                    <button
                      className="rounded-md border border-border px-3 py-1.5 text-xs text-muted-foreground hover:bg-muted"
                      onClick={() => {
                        setShowCustomCalendar(false);
                        setPeriodPopoverOpen(false);
                      }}
                    >
                      Cancel
                    </button>
                    <button
                      className="rounded-md bg-slate-800 px-3 py-1.5 text-xs font-medium text-white hover:bg-slate-700 disabled:opacity-50"
                      disabled={!queueDraftRange?.from || !queueDraftRange?.to}
                      onClick={handleQueueCustomApply}
                    >
                      Apply
                    </button>
                  </div>
                </>
              )}
            </PopoverContent>
          </Popover>

          {/* Refresh */}
          <div className="flex items-center gap-2">
            <button
              onClick={handleManualRefresh}
              disabled={activityResult.loading}
              className="flex items-center justify-center rounded-md border border-border bg-card p-2 text-muted-foreground hover:text-foreground disabled:opacity-50"
              aria-label="Refresh"
            >
              <RefreshCw className={`size-4 ${activityResult.loading ? "animate-spin" : ""}`} />
            </button>
            <span className="text-[11px] leading-tight text-muted-foreground">
              Updated
              <br />
              <span className="font-medium text-emerald-600">{updatedLabel}</span>
            </span>
          </div>
        </div>
      </div>

      <div className="flex-1 overflow-auto [scrollbar-gutter:stable]">
        {/* Error banner */}
        {activityResult.error && (
          <div className="mx-6 mt-4 rounded-md border border-rose-200 bg-rose-50 px-4 py-2 text-sm text-rose-700">
            {activityResult.error}
          </div>
        )}

        {/* Content */}
        <div className="mx-auto flex w-full max-w-[1200px] flex-col gap-4 px-6 py-5">
          <h1 className="text-[22px] font-semibold text-foreground">All Requests</h1>

          {activityResult.loading && !activityResult.data ? (
            <div className="flex h-40 items-center justify-center">
              <Spinner />
            </div>
          ) : (
            <AllRequestsTable
              activities={activities}
              onRowClick={(row) =>
                navigate(`/underwriting-queue/${row.applicationId}`, {
                  state: { activity: row, origin: { path: "/underwriting-queue/all-requests", label: "All Requests" } },
                })
              }
              currentUserName={currentUser?.name ?? null}
              onClaim={handleClaim}
            />
          )}
        </div>
      </div>
    </div>
  );
}
